import type { WorkspaceViewDataset } from '@parcel/filters';
import type { WorkspaceView } from './workspace-views.js';

export const favoriteCollapseKey = (userId: string, workspaceId: string) =>
  `parcel:favorites:collapsed:${userId}:${workspaceId}`;

export function favoriteViewTo(workspaceSlug: string, view: Pick<WorkspaceView, 'dataset'>) {
  const plural = `${view.dataset}s`;
  return `/${encodeURIComponent(workspaceSlug)}/${plural}`;
}

export function favoriteViewPath(
  workspaceSlug: string,
  view: Pick<WorkspaceView, 'id' | 'dataset'>,
) {
  return `${favoriteViewTo(workspaceSlug, view)}?view=${encodeURIComponent(view.id)}`;
}

/**
 * The favorited View the current URL is showing, or null.
 *
 * Both halves have to agree. The id has to name a View that is actually in Favorites, and
 * the path has to be that View's own dataset page, so a `?view=` left over on another
 * entity's route cannot light up a row that is not what you are looking at.
 */
export function activeFavoriteViewId(
  favorites: readonly Pick<WorkspaceView, 'id' | 'dataset'>[],
  workspaceSlug: string | null,
  pathname: string,
  urlViewId: string | null,
): string | null {
  if (!urlViewId || !workspaceSlug) return null;
  const match = favorites.find((view) => view.id === urlViewId);
  if (!match) return null;
  return favoriteViewTo(workspaceSlug, match) === pathname ? match.id : null;
}

export function favoriteDatasetLabel(dataset: WorkspaceViewDataset): string {
  return `${dataset.charAt(0).toUpperCase()}${dataset.slice(1)}`;
}

/** Missing key defaults to expanded (`false`). */
export function readFavoriteCollapsed(storage: Storage | null | undefined, key: string): boolean {
  if (!storage) return false;
  return storage.getItem(key) === 'true';
}

export function writeFavoriteCollapsed(
  storage: Storage | null | undefined,
  key: string,
  collapsed: boolean,
): void {
  if (!storage) return;
  storage.setItem(key, collapsed ? 'true' : 'false');
}
